import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { TodayCounts, TodayFeedItem, TodayFeedKind } from '../../services/crmService';
import LeadTemperatureBadge from './LeadTemperatureBadge';
import type { LeadTemperature } from '../../services/crmService';
import { leadsPipelineUrl } from '../../utils/crmLinks';
import DashboardCard from './DashboardCard';

const KIND_LABELS: Record<TodayFeedKind, string> = {
  overdue: 'Overdue',
  due_today: 'Due today',
  needs_review: 'Needs review',
  new_inbound: 'New',
};

export const TODAY_HERO_MAX = 5;

export interface TodayHeroProps {
  items: TodayFeedItem[];
  counts: TodayCounts | null;
  loading: boolean;
  onQuickAdd?: () => void;
}

const TodayHero: React.FC<TodayHeroProps> = ({
  items,
  counts,
  loading,
  onQuickAdd,
}) => {
  const grouped = useMemo(() => {
    const order: TodayFeedKind[] = ['overdue', 'due_today', 'needs_review', 'new_inbound'];
    const buckets = new Map<TodayFeedKind, TodayFeedItem[]>();
    for (const kind of order) {
      buckets.set(kind, []);
    }
    for (const item of items) {
      buckets.get(item.kind)?.push(item);
    }
    return order
      .map((kind) => ({ kind, items: buckets.get(kind) ?? [] }))
      .filter((g) => g.items.length > 0);
  }, [items]);

  const shownFollowUp = items.filter((i) => i.kind === 'overdue' || i.kind === 'due_today').length;
  const shownNeedsReview = items.filter((i) => i.kind === 'needs_review').length;
  const shownNew = items.filter((i) => i.kind === 'new_inbound').length;

  const followUpTotal = counts ? counts.overdue + counts.due_today : 0;
  const followUpHidden = Math.max(0, followUpTotal - shownFollowUp);
  const needsReviewHidden = counts ? Math.max(0, counts.needs_review - shownNeedsReview) : 0;
  const newHidden = counts ? Math.max(0, counts.new - shownNew) : 0;
  const totalHidden = counts ? Math.max(0, counts.total - items.length) : 0;

  return (
    <DashboardCard
      title="Today"
      subtitle="Who to contact now — overdue, due today, and new since your last visit."
    >
      {loading ? (
          <p className="text-sm text-fg-muted">Loading…</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center rounded-md border border-dashed border-border bg-app/50 px-4 py-8">
            <p className="text-sm font-medium text-fg">You&apos;re clear for now</p>
            <p className="text-xs text-fg-secondary mt-2 max-w-md">
              When follow-ups are due or new leads arrive, they&apos;ll show up here first — not buried
              in the middle of the page.
            </p>
            {onQuickAdd ? (
              <button
                type="button"
                onClick={onQuickAdd}
                className="mt-4 btn-primary text-sm py-2 px-4 rounded-md"
              >
                Quick Add
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
              {grouped.map(({ kind, items: groupItems }) => (
                <div key={kind}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-muted mb-2">
                    {KIND_LABELS[kind]}
                  </h3>
                  <ul className="space-y-1.5">
                    {groupItems.map((item) => (
                      <li key={`${item.kind}-${item.lead_id ?? item.client_id}-${item.urgency_rank}`}>
                        <Link
                          to={item.href}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 rounded-md border border-border p-2.5 hover:bg-app/80 transition-colors min-w-0"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-fg truncate">{item.name}</div>
                            {item.owner ? (
                              <div className="text-xs text-fg-muted truncate">{item.owner}</div>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap">
                            <span className="text-xs text-fg-secondary">{item.reason}</span>
                            {item.temperature ? (
                              <LeadTemperatureBadge temperature={item.temperature as LeadTemperature} />
                            ) : null}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {counts && totalHidden > 0 ? (
              <div className="mt-4 pt-3 border-t border-border flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-sm">
                {followUpHidden > 0 ? (
                  <Link
                    to={leadsPipelineUrl({ follow_up: 'today' })}
                    className="font-medium text-primary hover:underline"
                  >
                    +{followUpHidden} more due today / overdue →
                  </Link>
                ) : null}
                {needsReviewHidden > 0 ? (
                  <Link
                    to={leadsPipelineUrl({ requires_review: true })}
                    className="font-medium text-primary hover:underline"
                  >
                    +{needsReviewHidden} more needs review →
                  </Link>
                ) : null}
                {newHidden > 0 ? (
                  <Link to="/leads?tab=inbound" className="font-medium text-primary hover:underline">
                    +{newHidden} more new inbound →
                  </Link>
                ) : null}
              </div>
            ) : null}
          </>
        )}
    </DashboardCard>
  );
};

export default TodayHero;
